import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, Copy, Check, Instagram, Facebook, Sparkles, BookOpen, CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { PUBLISHED_POSTS } from "@/lib/blog-posts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function joinCopy(caption, hashtags) {
  const tags = Array.isArray(hashtags) && hashtags.length ? `\n\n${hashtags.join(" ")}` : "";
  return `${caption || ""}${tags}`.trim();
}

function CopyButton({ text, testid }) {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast.error("Couldn't copy — select the text manually");
    }
  };
  return (
    <Button onClick={copy} size="sm" variant="outline" className="gap-1.5" data-testid={testid}>
      {done ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {done ? "Copied" : "Copy"}
    </Button>
  );
}

function PlatformBlock({ icon: Icon, label, post, color, testidPrefix }) {
  if (!post || (!post.caption && !post.hashtags)) return null;
  const full = joinCopy(post.caption, post.hashtags);
  return (
    <div className={`rounded-2xl border ${color} p-4`} data-testid={`${testidPrefix}-block`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-semibold text-slate-800">
          <Icon className="h-4 w-4" />
          <span>{label}</span>
        </div>
        <CopyButton text={full} testid={`${testidPrefix}-copy`} />
      </div>
      <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{post.caption}</p>
      {Array.isArray(post.hashtags) && post.hashtags.length > 0 && (
        <p className="mt-3 text-sm text-blue-700 break-words" data-testid={`${testidPrefix}-hashtags`}>
          {post.hashtags.join(" ")}
        </p>
      )}
    </div>
  );
}

function ResultCard({ result, headerLabel, testid }) {
  if (!result) return null;
  return (
    <Card className="p-5 space-y-4" data-testid={testid}>
      {headerLabel && (
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">{headerLabel}</div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <PlatformBlock
          icon={Instagram}
          label="Instagram"
          post={result.instagram}
          color="border-pink-200 bg-pink-50/40"
          testidPrefix={`${testid}-ig`}
        />
        <PlatformBlock
          icon={Facebook}
          label="Facebook"
          post={result.facebook}
          color="border-blue-200 bg-blue-50/40"
          testidPrefix={`${testid}-fb`}
        />
      </div>
    </Card>
  );
}

function Generator({ token }) {
  const [topics, setTopics] = useState([]);
  const [topic, setTopic] = useState("");
  const [extra, setExtra] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/social/topics`, { headers: { "X-Admin-Token": token } })
      .then(({ data }) => {
        setTopics(data.topics || []);
        if (data.topics?.[0]) setTopic(data.topics[0].key);
      })
      .catch(() => toast.error("Failed to load topics"));
  }, [token]);

  const generate = async () => {
    if (!topic) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/social/generate`,
        { topic, extra_context: extra },
        { headers: { "X-Admin-Token": token } },
      );
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="social-generator">
      <Card className="p-5 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-slate-800 mb-1.5">Topic</label>
          <select
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
            data-testid="social-topic-select"
          >
            {topics.map((t) => (
              <option key={t.key} value={t.key}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-800 mb-1.5">
            Extra context <span className="font-normal text-slate-500">(optional — e.g. "tie this to Valentine's Day")</span>
          </label>
          <Textarea
            value={extra}
            onChange={(e) => setExtra(e.target.value)}
            placeholder="Add a date, promo dates, or specific detail you want the post to mention…"
            rows={2}
            data-testid="social-extra-input"
          />
        </div>
        <Button onClick={generate} disabled={busy || !topic} className="gap-2" data-testid="social-generate-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {busy ? "Drafting…" : "Generate post"}
        </Button>
      </Card>

      {result && (
        <ResultCard
          result={result}
          headerLabel={result.topic_label}
          testid="social-result"
        />
      )}
    </div>
  );
}

function FromBlog({ token }) {
  const [slug, setSlug] = useState(PUBLISHED_POSTS[0]?.slug || "");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const current = PUBLISHED_POSTS.find((p) => p.slug === slug);

  const generate = async () => {
    if (!current) return;
    setBusy(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/social/from-blog`,
        { title: current.title, excerpt: current.excerpt, slug: current.slug },
        { headers: { "X-Admin-Token": token } },
      );
      setResult(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="social-from-blog">
      <Card className="p-5 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-slate-800 mb-1.5">Blog post</label>
          <select
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
            data-testid="social-blog-select"
          >
            {PUBLISHED_POSTS.map((p) => (
              <option key={p.slug} value={p.slug}>
                {p.title}
              </option>
            ))}
          </select>
        </div>
        {current && (
          <p className="text-xs text-slate-500 italic line-clamp-3">"{current.excerpt}"</p>
        )}
        <Button onClick={generate} disabled={busy || !current} className="gap-2" data-testid="social-blog-generate">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
          {busy ? "Drafting…" : "Draft social posts from this article"}
        </Button>
      </Card>
      <ResultCard result={result} headerLabel={result?.blog_title} testid="blog-social-result" />
    </div>
  );
}

function Calendar({ token }) {
  const [weeks, setWeeks] = useState(4);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState(null);
  const [progress, setProgress] = useState("");

  const generate = useCallback(async () => {
    setBusy(true);
    setItems(null);
    setProgress(`Generating ${weeks} weeks of content (this can take 30-60 seconds)…`);
    try {
      const { data } = await axios.post(
        `${API}/admin/social/calendar`,
        { weeks },
        { headers: { "X-Admin-Token": token }, timeout: 180000 },
      );
      setItems(data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate calendar");
    } finally {
      setBusy(false);
      setProgress("");
    }
  }, [weeks, token]);

  return (
    <div className="space-y-5" data-testid="social-calendar">
      <Card className="p-5 space-y-4">
        <div>
          <label className="block text-sm font-semibold text-slate-800 mb-1.5">
            How many weeks of content?
          </label>
          <div className="flex items-center gap-3">
            <select
              value={weeks}
              onChange={(e) => setWeeks(Number(e.target.value))}
              className="h-10 rounded-md border border-slate-300 bg-white px-3 text-sm w-24"
              data-testid="calendar-weeks-select"
            >
              {[2, 3, 4, 6, 8].map((n) => (
                <option key={n} value={n}>
                  {n} weeks
                </option>
              ))}
            </select>
            <Button onClick={generate} disabled={busy} className="gap-2" data-testid="calendar-generate-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarDays className="h-4 w-4" />}
              {busy ? "Generating…" : "Generate calendar"}
            </Button>
          </div>
          <p className="text-xs text-slate-500 mt-2">
            We'll draft 1 Instagram + 1 Facebook post per week, rotating across promos, tips,
            veteran content and customer-acquisition topics.
          </p>
        </div>
        {progress && <div className="text-sm text-slate-600">{progress}</div>}
      </Card>

      {items && items.length > 0 && (
        <div className="space-y-4">
          {items.map((slot) => (
            <div key={slot.week} data-testid={`calendar-slot-${slot.week}`}>
              <div className="flex items-center gap-3 mb-2">
                <span className="inline-flex items-center justify-center h-7 min-w-[3.5rem] rounded-full bg-brand-navy text-white text-xs font-bold px-2.5">
                  Week {slot.week}
                </span>
                <span className="text-sm font-semibold text-slate-700">{slot.topic_label}</span>
              </div>
              {slot.error ? (
                <Card className="p-4 text-sm text-red-700 bg-red-50 border-red-200">
                  Failed to generate: {slot.error}
                </Card>
              ) : (
                <ResultCard result={slot} headerLabel={null} testid={`calendar-result-${slot.week}`} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function SocialPosts({ token }) {
  return (
    <div data-testid="panel-social">
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-slate-900">Social Media Posts</h2>
        <p className="text-sm text-slate-500">
          AI-drafted Instagram & Facebook captions you can copy straight into Meta Business Suite.
        </p>
      </div>

      <Tabs defaultValue="generator">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="generator" data-testid="social-tab-generator">Generator</TabsTrigger>
          <TabsTrigger value="from-blog" data-testid="social-tab-from-blog">From Blog</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="social-tab-calendar">Calendar</TabsTrigger>
        </TabsList>
        <TabsContent value="generator" className="mt-5">
          <Generator token={token} />
        </TabsContent>
        <TabsContent value="from-blog" className="mt-5">
          <FromBlog token={token} />
        </TabsContent>
        <TabsContent value="calendar" className="mt-5">
          <Calendar token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
